import gql from 'graphql-tag';
import { User, PublicUserInfo } from '../../../types/user';
import { Permission } from '../../../types/permissionGroup';

/**
 * Response type for the MY_USER query.
 */
export type MyUserResponse = {
  myUser: User;
};

/**
 * Variables type for the USER_AND_PERMISSIONS query.
 * All fields are optional - at least one should be provided to query a user.
 */
export type UserAndPermissionsVariables = {
  userId?: string;
  email?: string;
  preferredUsername?: string;
};

/**
 * Response type for the USER_AND_PERMISSIONS query.
 */
export type UserAndPermissionsResponse = {
  user: User;
  userPermissions: Permission[];
};

/**
 * Variables type for the SEARCH_STAFF_USERS query.
 */
export type SearchStaffUsersVariables = {
  input: string;
};

/**
 * Staff user info returned from search queries.
 * Contains minimal fields needed for staff user listings.
 */
export type StaffUserInfo = {
  userId: string;
  preferredUsername: string;
  avatar?: {
    sourceUrl: string;
    sourceSet: string;
  };
};

/**
 * Response type for the SEARCH_STAFF_USERS query.
 */
export type SearchStaffUsersResponse = {
  searchUsers: StaffUserInfo[];
};

/**
 * GraphQL query for fetching the currently authenticated user's information.
 *
 * Returns complete user profile including email, username, avatar, banner,
 * social media links, and permission group information.
 *
 * @example
 * ```ts
 * import { MY_USER, MyUserResponse } from '@elevator/ui';
 *
 * const { data, loading } = useQuery<MyUserResponse>(MY_USER);
 *
 * if (data) {
 *   const user = data.myUser;
 *   console.log(user.email, user.preferredUsername);
 * }
 * ```
 */
export const MY_USER = gql`
  query myUser {
    myUser {
      userId
      email
      emailVerified
      username
      preferredUsername
      displayName
      firstName
      lastName
      role
      title {
        typeId
        typeName
      }
      avatar {
        path
        sourceUrl
        sourceSet
        metadata {
          bucket
          mimetype
          s3Url
          filename
        }
      }
      banner {
        path
        sourceUrl
        sourceSet
        metadata {
          bucket
          mimetype
          s3Url
          filename
        }
      }
      bio
      socialMedia {
        type
        link
      }
      disabled
      preferred
      createdAt
      updatedAt
      permissionGroup {
        groupId
        groupName
      }
    }
  }
`;

/**
 * GraphQL query for fetching a user's profile and permissions.
 *
 * Can query by userId, email, or preferredUsername (at least one required).
 * Returns both the user profile and their detailed permissions array.
 *
 * @example
 * ```ts
 * import { USER_AND_PERMISSIONS, UserAndPermissionsResponse, UserAndPermissionsVariables } from '@elevator/ui';
 *
 * const { data, loading } = useQuery<UserAndPermissionsResponse, UserAndPermissionsVariables>(
 *   USER_AND_PERMISSIONS,
 *   {
 *     variables: { preferredUsername: 'john_doe' }
 *   }
 * );
 *
 * if (data) {
 *   const user = data.user;
 *   const permissions = data.userPermissions;
 * }
 * ```
 */
export const USER_AND_PERMISSIONS = gql`
  query user($userId: ID, $email: String, $preferredUsername: String) {
    user(userId: $userId, email: $email, preferredUsername: $preferredUsername) {
      userId
      email
      emailVerified
      username
      preferredUsername
      firstName
      lastName
      role
      avatar {
        path
        sourceUrl
        sourceSet
        metadata {
          bucket
          mimetype
          s3Url
          filename
        }
      }
      bio
      socialMedia {
        type
        link
      }
      disabled
      preferred
      createdAt
      updatedAt
      permissionGroup {
        groupId
        groupName
        groupDesc
        permissionLevel
      }
    }
    userPermissions(userId: $userId, preferredUsername: $preferredUsername) {
      permissionId
      access
      operationName
      description
      type
      permissionGroup {
        groupId
        groupName
        groupDesc
        permissionLevel
      }
    }
  }
`;

/**
 * GraphQL query for searching staff users (Admin and Team permission groups).
 *
 * Searches users by input string and filters to only include users with
 * Admin or Team permission groups. Returns minimal user info for listings.
 *
 * @example
 * ```ts
 * import { SEARCH_STAFF_USERS, SearchStaffUsersResponse, SearchStaffUsersVariables } from '@elevator/ui';
 *
 * const { data, loading } = useQuery<SearchStaffUsersResponse, SearchStaffUsersVariables>(
 *   SEARCH_STAFF_USERS,
 *   {
 *     variables: { input: 'john' }
 *   }
 * );
 *
 * if (data) {
 *   const staffUsers = data.searchUsers;
 * }
 * ```
 */
export const SEARCH_STAFF_USERS = gql`
  query searchStaffUsers($input: String!) {
    searchUsers(input: $input, permissionGroups: ["Admin", "Team"]) {
      userId
      preferredUsername
      avatar {
        sourceUrl
        sourceSet
      }
    }
  }
`;
